import type { ProgressionConfig, ProgressionTier, XpBoost } from './types.js';

/**
 * The status ladder — pure helpers over {@link ProgressionConfig}.
 *
 * Like the rest of the economy this stays free of React and the DOM, and it
 * also stays free of `logic.ts`: granting XP (which pays tier rewards out) lives
 * there with the other mutations, while everything here just reads the ladder.
 * Both the game client and the console derive "what status is this" from these
 * same functions, so the two can never disagree on where a threshold sits.
 */

/**
 * The default ladder.
 *
 * **One credit delivered = one XP.** Credits already track money at 20 per
 * dollar, so XP rides the same numbers the owner and the players already know —
 * 20 XP per dollar of confirmed business. Each tier is a lifetime-spend
 * milestone: Bronze ≈ $20, Silver ≈ $50, Gold ≈ $150, Platinum ≈ $400,
 * Diamond ≈ $1,000 — anchored on the reward catalogue (tiers run $10 to $100,
 * so a regular reaches Bronze on their first real purchase and Diamond marks a
 * whale). Spins feed the same scale through their cost, so playing counts
 * toward status too. Nothing is earned for merely showing up.
 *
 * Black is hidden: it never shows on the track until held, and nobody is told
 * it exists. A rumour is cheaper than a reward and sells better.
 */
export const DEFAULT_TIERS: ProgressionTier[] = [
  {
    id: 'member',
    name: 'Member',
    xp: 0,
    creditBonus: 0,
    couponPercent: 0,
    perks: ['Access to the club'],
    hidden: false,
  },
  {
    id: 'bronze',
    name: 'Bronze',
    xp: 400,
    creditBonus: 50,
    couponPercent: 5,
    perks: ['Bronze colour in the club'],
    hidden: false,
  },
  {
    id: 'silver',
    name: 'Silver',
    xp: 1000,
    creditBonus: 100,
    couponPercent: 10,
    perks: ['Silver colour in the club'],
    hidden: false,
  },
  {
    id: 'gold',
    name: 'Gold',
    xp: 3000,
    creditBonus: 250,
    couponPercent: 15,
    perks: ['Early access to drops'],
    hidden: false,
  },
  {
    id: 'platinum',
    name: 'Platinum',
    xp: 8000,
    creditBonus: 500,
    couponPercent: 15,
    perks: ['Priority DM replies', 'Access to reserved offers'],
    hidden: false,
  },
  {
    id: 'diamond',
    name: 'Diamond',
    xp: 20000,
    creditBonus: 1000,
    couponPercent: 20,
    perks: ['A monthly pick from the vault', 'Top of the club'],
    hidden: false,
  },
  {
    id: 'black',
    name: 'Black',
    xp: 50000,
    creditBonus: 2500,
    couponPercent: 25,
    perks: ['You found it.'],
    hidden: true,
  },
];

/** A fresh copy of the default progression, safe to mutate per profile. */
export function seedProgression(): ProgressionConfig {
  return {
    xpPerCreditDelivered: 1,
    xpPerCreditSpent: 1,
    tiers: DEFAULT_TIERS.map((tier) => ({ ...tier, perks: [...tier.perks] })),
    boost: null,
  };
}

/* ------------------------------------------------------------------ *
 * The happy hour.
 * ------------------------------------------------------------------ */

/** The boost if one is running at `now`, or null. Expiry is read, never stored. */
export function activeBoost(config: Pick<ProgressionConfig, 'boost'>, now: number): XpBoost | null {
  const boost = config.boost;
  if (!boost) return null;
  return new Date(boost.until).getTime() > now ? boost : null;
}

/**
 * An earned XP amount after any running happy hour.
 *
 * Applied to XP the player *earned*; owner grants deliberately bypass it — see
 * {@link XpBoost}.
 */
export function boostedXp(
  config: Pick<ProgressionConfig, 'boost'>,
  amount: number,
  now: number,
): number {
  const boost = activeBoost(config, now);
  return boost ? Math.round(amount * boost.multiplier) : amount;
}

/**
 * Readers take anything that carries `tiers`, not the whole config: the game
 * client only ever receives the projected track, and it must be able to derive
 * current/next status from it with these same helpers.
 */
type Ladder = Pick<ProgressionConfig, 'tiers'>;

/** The ladder sorted ascending by threshold — the order every reader assumes. */
export function sortedTiers(config: Ladder): ProgressionTier[] {
  return [...config.tiers].sort((a, b) => a.xp - b.xp);
}

/** The status this much XP holds: the highest tier at or below it. */
export function tierForXp(config: Ladder, xp: number): ProgressionTier {
  const tiers = sortedTiers(config);
  let current = tiers[0] ?? DEFAULT_TIERS[0]!;
  for (const tier of tiers) {
    if (tier.xp <= xp) current = tier;
  }
  return current;
}

/** The next rung up, or null at the top of the ladder. */
export function nextTierFor(config: Ladder, xp: number): ProgressionTier | null {
  return sortedTiers(config).find((tier) => tier.xp > xp) ?? null;
}

/**
 * The ladder as one player may see it: every visible tier, plus any hidden
 * one they have already reached. A hidden tier a player hasn't earned is not
 * greyed out — it is absent, which is what keeps it a rumour.
 */
export function visibleTrack(config: Ladder, xp: number): ProgressionTier[] {
  return sortedTiers(config).filter((tier) => !tier.hidden || tier.xp <= xp);
}

/** The tiers crossed by moving from `before` to `after` XP, ascending. */
export function tiersCrossed(config: Ladder, before: number, after: number): ProgressionTier[] {
  return sortedTiers(config).filter((tier) => tier.xp > before && tier.xp <= after);
}

/**
 * The same ladder wearing a different set of names.
 *
 * Renames rungs **in ascending threshold order** and touches nothing else: the
 * thresholds, the credit bonuses, the coupons, the perks and the hidden flag all
 * survive, because a creator who has tuned what Gold pays should not lose it by
 * dressing her realm. A ladder longer than the name list keeps its extra names.
 *
 * The counterpart of `withPalette` for the wheel, and applied the same way —
 * from one explicit click in the console, never on load.
 */
export function withLadderNames(tiers: ProgressionTier[], names: string[]): ProgressionTier[] {
  const order = sortedTiers({ tiers });
  const renamed = new Map(
    order.map((tier, index) => [tier.id, names[index] ?? tier.name] as const),
  );
  return tiers.map((tier) => ({ ...tier, name: renamed.get(tier.id) ?? tier.name }));
}

/** Backfills a persisted progression config, so older saves load unchanged. */
export function progressionWithDefaults(parsed: Partial<ProgressionConfig> | undefined) {
  const seed = seedProgression();
  const rate = (input: unknown, fallback: number): number =>
    typeof input === 'number' && Number.isFinite(input) && input >= 0 ? input : fallback;

  const tiers =
    Array.isArray(parsed?.tiers) && parsed.tiers.length > 0
      ? parsed.tiers.map((raw, index) => ({
          id: typeof raw?.id === 'string' && raw.id ? raw.id : `tier${index}`,
          name: typeof raw?.name === 'string' && raw.name ? raw.name : `Tier ${index + 1}`,
          xp: rate(raw?.xp, 0),
          creditBonus: Math.round(rate(raw?.creditBonus, 0)),
          couponPercent: Math.min(100, Math.round(rate(raw?.couponPercent, 0))),
          perks: Array.isArray(raw?.perks)
            ? raw.perks.filter((perk): perk is string => typeof perk === 'string')
            : [],
          hidden: raw?.hidden === true,
          // A realm rung used to carry a Discord role id. Realm tiers are rewards
          // now and roles live only on the server-wide ladder, so a saved one is
          // simply not read — and is dropped on the next write.
        }))
      : seed.tiers;

  const raw = parsed?.boost;
  const boost: XpBoost | null =
    raw && typeof raw.until === 'string' && typeof raw.multiplier === 'number'
      ? {
          multiplier: Math.min(10, Math.max(1, raw.multiplier)),
          until: raw.until,
          label: typeof raw.label === 'string' && raw.label ? raw.label : 'Happy hour',
        }
      : null;

  return {
    xpPerCreditDelivered: rate(parsed?.xpPerCreditDelivered, seed.xpPerCreditDelivered),
    xpPerCreditSpent: rate(parsed?.xpPerCreditSpent, seed.xpPerCreditSpent),
    tiers,
    boost,
  } satisfies ProgressionConfig;
}

/** One rung of a realm's battle pass as its model submits it. A missing `id` means a new rung. */
export interface RealmTierEdit {
  id?: string;
  name: string;
  xp: number;
  creditBonus: number;
  couponPercent: number;
  perks: string[];
  hidden: boolean;
}

export type RealmLadderEdit = { ok: true; tiers: ProgressionTier[] } | { ok: false; error: string };

/** The most rungs a battle pass may have. */
export const MAX_REALM_TIERS = 30;

/**
 * Validates an edit to a realm's battle pass and applies it.
 *
 * This is the one place a model retunes **what a rung takes and what it pays** —
 * threshold, credit bonus, coupon, perks, visibility — and adds or removes rungs.
 * It is deliberately separate from the names-only route that dresses a realm in
 * a theme's words, so that one click can never move an economy. Nothing here
 * touches a Discord role: realm tiers are rewards to win, and roles live only on
 * the server-wide ladder.
 *
 * Refused rather than repaired, for the reason the global ladder is: a ladder
 * that quietly re-sorted itself would hand people rewards nobody set.
 *
 * - the entry rung must sit at 0, since every account holds some rung and the
 *   club shows it;
 * - two rungs may not share a threshold;
 * - coupons are 0–100% and credit bonuses are whole, non-negative amounts.
 *
 * One consequence worth knowing: rewards are paid when XP *crosses* a rung, so
 * raising a threshold above someone who already passed it will pay them again on
 * their next gain. Lowering one pays nobody retroactively.
 */
export function editRealmLadder(
  current: readonly ProgressionTier[],
  edits: unknown,
  newId: () => string,
): RealmLadderEdit {
  if (!Array.isArray(edits) || edits.length === 0) {
    return { ok: false, error: 'A battle pass needs at least one rung' };
  }
  if (edits.length > MAX_REALM_TIERS) {
    return { ok: false, error: `A battle pass may have at most ${MAX_REALM_TIERS} rungs` };
  }

  const known = new Set(current.map((tier) => tier.id));
  const ids = new Set<string>();
  const thresholds = new Set<number>();
  const tiers: ProgressionTier[] = [];

  const whole = (input: unknown, max: number): number | null =>
    typeof input === 'number' && Number.isFinite(input) && input >= 0 && input <= max
      ? Math.round(input)
      : null;

  for (const raw of edits) {
    if (typeof raw !== 'object' || raw === null) return { ok: false, error: 'Malformed rung' };
    const edit = raw as Partial<RealmTierEdit>;

    const name = typeof edit.name === 'string' ? edit.name.trim().slice(0, 40) : '';
    if (!name) return { ok: false, error: 'Every rung needs a name' };

    const xp = whole(edit.xp, 100_000_000);
    if (xp === null) return { ok: false, error: `"${name}": the threshold must be a whole number` };
    if (thresholds.has(xp)) {
      return { ok: false, error: `Two rungs sit at ${xp} — give each its own threshold` };
    }
    thresholds.add(xp);

    const creditBonus = whole(edit.creditBonus ?? 0, 100_000_000);
    if (creditBonus === null)
      return { ok: false, error: `"${name}": the bonus must be a whole number` };
    const couponPercent = whole(edit.couponPercent ?? 0, 100);
    if (couponPercent === null) return { ok: false, error: `"${name}": a coupon is 0–100%` };

    let id: string;
    if (typeof edit.id === 'string' && edit.id) {
      if (!known.has(edit.id)) {
        return {
          ok: false,
          error: 'This battle pass changed somewhere else — reload and try again',
        };
      }
      id = edit.id;
    } else {
      id = newId();
    }
    if (ids.has(id)) return { ok: false, error: 'A rung appears twice' };
    ids.add(id);

    const perks = Array.isArray(edit.perks)
      ? edit.perks
          .filter((perk): perk is string => typeof perk === 'string')
          .map((perk) => perk.trim().slice(0, 120))
          .filter(Boolean)
          .slice(0, 10)
      : [];

    tiers.push({ id, name, xp, creditBonus, couponPercent, perks, hidden: edit.hidden === true });
  }

  if (!thresholds.has(0)) {
    return { ok: false, error: 'The entry rung must sit at 0 — everyone starts somewhere' };
  }
  return { ok: true, tiers: tiers.sort((a, b) => a.xp - b.xp) };
}
